import React, { useState } from 'react';
import classnames from 'classnames';
import { Button, FormGroup } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { FEATURE_FLAGS } from 'utils/constants';
import exclamationCircle from 'v2//icons/exclamationCircle.svg';
import DataFileBoxV2 from 'v2/DataFiles/DataFileBoxV2/DataFileBoxV2';
import Checkbox from 'v2/form/Checkbox/Checkbox';
import trashCanIcon from 'v2/icons/trash-outlined.svg';
import {
  ProposalEntity,
  ProviderProposalResponseFields,
  RootState,
  SearchEntity,
} from 'v2/models';
import ProposalAttachFilesModal from './ProposalAttachFilesModal';
import ProposalMainResponse from './ProposalMainResponse';
import ProposalQuestions from './ProposalQuestions';
import styles from './proposalRespondModal.module.scss';
import ProviderProfile from './ProviderProfile';
import { useFeatureFlagContext } from 'v2/context/featureFlagContext';
import useStoreProposal from 'v2/queries/useProposalStored';

interface ProposalRespondContentProps {
  proposal: ProposalEntity;
  isDetailsFormat?: boolean;
}

const ProposalRespondContent = ({
  proposal,
  isDetailsFormat = false,
}: ProposalRespondContentProps) => {
  const user = useSelector((state: RootState) => state.user.user);
  const { flags } = useFeatureFlagContext();
  const isCheckSellerResponseEnabled =
    flags && flags[FEATURE_FLAGS.CHECK_SELLER_RESPONSE];
  const [showAttachFilesModal, setShowAttachFilesModal] = useState(false);
  const search = proposal.search as SearchEntity;

  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
    getValues,
  } = useFormContext<ProviderProposalResponseFields>();
  const formValues = getValues();

  const hideIdentityWatched = watch('hide_identity');

  useStoreProposal(proposal, watch);

  return (
    <div
      className={classnames(
        'px-0 px-lg-3 py-4 w-100 w-lg-50',
        styles.rightSide,
        { [styles.isDetailsFormat]: isDetailsFormat },
        { [styles.titles]: isDetailsFormat }
      )}
      data-testid="proposalRespondContent"
    >
      <h1>Your Response</h1>
      <p className={classnames('text-light8', styles.sectionDescription)}>
        Please provide a detailed response below.
      </p>

      <div className="bg-light px-3 pb-2">
        <img
          className={styles.infoIcon}
          src={exclamationCircle}
          alt="exclamation"
        ></img>
        <h6
          className={classnames(
            'text-primary-dark font-weight-bold pl-4',
            styles.goodResponses
          )}
        >
          Characteristics of good responses:
        </h6>
        <p className="text-primary-dark pl-4 font-14 mb-0">
          Avoid vague answers such as “Yes we have the data”. Avoid sharing
          contact information until after your offer has been accepted.
        </p>
      </div>

      <div className={classnames('mt-4 pb-4', styles.proposalSection)}>
        <h5 className="font-weight-bold">
          How does your data product address the buyer’s need?
        </h5>
        <ProposalMainResponse
          errors={errors}
          register={register}
          control={control}
          isCheckSellerResponseEnabled={isCheckSellerResponseEnabled}
          searchDescription={search.description}
        />
      </div>

      {search.questions.length ? (
        <>
          <hr className="my-1" />
          <div className="pt-4">
            <h1>Additional Questions</h1>
            <p
              className={classnames(
                'text-light8 mb-5',
                styles.sectionDescription
              )}
            >
              Please answer the following questions below.
            </p>

            <ProposalQuestions
              control={control}
              errors={errors}
              register={register}
            />
          </div>
        </>
      ) : null}

      <hr className="my-1" />

      <FormGroup className="my-4">
        <h1>Attach Files</h1>
        <p
          className={classnames('text-light8 mt-2', styles.sectionDescription)}
        >
          Attach any necessary or helpful files below. You can upload new files
          or attach existing files from your library.
        </p>
        {formValues.data_files.length ? (
          <div>
            {formValues.data_files.map((dataFile) => (
              <div
                key={dataFile.id}
                className="mb-4 d-flex align-items-center justify-content-between"
              >
                <DataFileBoxV2
                  className={styles.dataFileBox}
                  data={dataFile}
                  allowDownload={true}
                />
                <Button
                  className={styles.deleteFileButton}
                  onClick={() => {
                    setValue(
                      'data_files',
                      formValues.data_files.filter(
                        (item) => dataFile.id !== item.id
                      ),
                      {
                        shouldValidate: true,
                        shouldDirty: true,
                      }
                    );
                  }}
                  aria-label="removeFileFromResponse"
                >
                  <img src={trashCanIcon} alt="deleteAttachedFile"></img>
                </Button>
              </div>
            ))}
          </div>
        ) : null}
        <Button
          className={classnames(
            'btn-secondary-dark',
            'mt-auto align-self-start',
            styles.chooseFileBtn
          )}
          variant="secondary"
          onClick={() => setShowAttachFilesModal(true)}
          title="Choose File"
        >
          Choose File
        </Button>
        <ProposalAttachFilesModal
          onHide={() => setShowAttachFilesModal(false)}
          show={showAttachFilesModal}
          onFilesAdded={(files) => {
            setValue('data_files', files, {
              shouldValidate: true,
              shouldDirty: true,
            });
            setShowAttachFilesModal(false);
          }}
          selectedFiles={formValues.data_files}
        />
      </FormGroup>

      <hr />

      {user.data_provider.hide_identity_allowed ? (
        <>
          <FormGroup className="my-5">
            <h1>Hide Your Identity</h1>
            <p
              className={classnames(
                'text-light8 mt-2',
                styles.sectionDescription
              )}
            >
              Hiding your identity may reduce the number of results you receive.
            </p>
            <Checkbox
              label=""
              register={register('hide_identity')}
              text="Hide my identity"
            />
          </FormGroup>
          <hr />
        </>
      ) : null}

      <ProviderProfile
        dataProvider={user.data_provider}
        hideIdentity={hideIdentityWatched}
      />
    </div>
  );
};

export default ProposalRespondContent;
